import { useFilters } from "../useFilters";

/**
 * Hook spécifique pour gérer les filtres des prospects/visits
 * @param resetPageCallback - Fonction pour réinitialiser la pagination
 * @param techniciansList - Liste globale des techniciens (optionnel)
 * @param customersList - Liste globale des clients (optionnel)
 * @returns - Interface pour manipuler les filtres
 */
export function useQuotationsFilters(
  resetPageCallback: () => void
) {
  // Fonction pour transformer les filtres avant sérialisation
  const transformForSerialization = (filters: Partial<EasyTech.Quotation>) => {
    const filtersCopy = { ...filters };

    // Traitement spécial pour les dates
    if (filtersCopy.dateRange) {
      if (filtersCopy.dateRange.start) {
        const startValue = filtersCopy.dateRange.start as any;
        const dateString = startValue instanceof Date
          ? startValue.toISOString()
          : new Date(startValue).toISOString();
        filtersCopy.dateRange = {
          ...filtersCopy.dateRange,
          start: dateString as any
        };
      }
      if (filtersCopy.dateRange.end) {
        const endValue = filtersCopy.dateRange.end as any;
        const dateString = endValue instanceof Date
          ? endValue.toISOString()
          : new Date(endValue).toISOString();
        filtersCopy.dateRange = {
          ...filtersCopy.dateRange,
          end: dateString as any
        };
      }
    }
    return filtersCopy;
  };

  // Fonction pour transformer les filtres après désérialisation
  const transformForDeserialization = (decodedFilters: any): Partial<EasyTech.Quotation> => {
    const filters = { ...decodedFilters };

    // Traitement spécial pour les dates - on les garde en format string
    // pour éviter les erreurs de type, elles seront converties en Date au moment
    // de leur utilisation dans les requêtes API
    if (filters.dateRange) {
      if (filters.dateRange.start) {
        // Garder en string, pas de conversion en Date pour éviter les erreurs de type
        filters.dateRange.start = filters.dateRange.start as any;
      }

      if (filters.dateRange.end) {
        // Garder en string, pas de conversion en Date pour éviter les erreurs de type
        filters.dateRange.end = filters.dateRange.end as any;
      }
    }

    if (filters.technician && Array.isArray(filters.technician)) {
      filters.technician = filters.technician.filter((id: any) => !!id);
    }
    if (filters.customer && Array.isArray(filters.customer)) {
      filters.customer = filters.customer.filter((id: any) => !!id);
    }
    return filters;
  };

  // Combine startDate et endDate en un seul objet dateRange pour l'affichage en badge unique
  const transformForDisplay = (filters: Partial<Partial<EasyTech.Quotation>>): Record<string, any> => {
    const displayFilters: Record<string, any> = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (key !== "startDate" && key !== "endDate") {
        displayFilters[key] = value;
      }
    });

    if ((filters as any).startDate || (filters as any).endDate) {
      displayFilters.dateRange = {
        start: (filters as any).startDate || null,
        end: (filters as any).endDate || null
      };
    }

    return displayFilters;
  };

  return useFilters<Partial<EasyTech.Quotation>>(
    resetPageCallback,
    {
      transformForSerialization,
      transformForDeserialization,
      transformForDisplay
    }
  );
} 